import type { Networkish, SupWeb3TokenAddress, SupWeb3TokenSymbol } from "@/types";
import type { Strategies } from "@/../config/swr.config";
import type { Address } from "viem";

import { useMemo } from "react";

import useApi from "@/hooks/api/useApi";
import useUnsupportedTokens from "@/hooks/api/useUnsupportedTokens";
import useWeb3UserAddress from "@/hooks/web3/useWeb3UserAddress";
import { FixedNumber } from "@ethersproject/bignumber";
import { getFormattedFiatAmount, getWeb3Address, roundBigNumberValueString } from "@/utils";

export interface SingleTokenBalance {
  id: SupWeb3TokenAddress;
  chain: Networkish;
  chain_id: Networkish;
  name: string;
  symbol: SupWeb3TokenSymbol;
  decimals: number;
  logo_url: string;
  amount: string;
  amount_corrected: string;
  price: number;
}

export interface ResponseTokenBalances {
  tokens: SingleTokenBalance[];
}

/**
 * @example icon is an url-address of the resource
 */
export interface SupWeb3Balance {
  chainId: Networkish;
  address: Address;
  name: string;
  symbol: SupWeb3TokenSymbol;
  decimals: number;
  icon: string;
  amount: string;
  // balance in hex
  price: number; // exchange rate in float
  chainName?: string;
  amountInFiat?: string;
  amountFixedNumber?: FixedNumber;
  searchIndex?: string; // @todo should be replaced with object search dep
  isUnsupported?: boolean;
}

/**
 * @package api:go
 * @link https://superform-xyz.postman.co/workspace/Superform~fe57d318-f198-4f97-9c50-0b2a5a78dbac/request/3472327-fa88bc58-fe7f-4a87-8d7d-1f0516bf41ed?ctx=documentation
 * @param {boolean} hideDust
 * @param {Strategies} strategy
 * @param {boolean} force
 * @param {number} timestamp
 */
export default function useTokenBalances(hideDust: boolean = false, strategy: Strategies = "on-mount", force: boolean = false, timestamp: number | undefined = undefined) {
  const userAddress = useWeb3UserAddress();

  const { data: unsupportedTokensList } = useUnsupportedTokens();

  const { data, isLoading, isError, mutate, error } = useApi<SupWeb3Balance[], ResponseTokenBalances>(
    {
      path: `/token/balances/${userAddress}/`,
      params: {
        force,
        timestamp,
      },
      api: "v2",
    },
    {
      enabled: !!userAddress && unsupportedTokensList && unsupportedTokensList.length > 0,
      strategy,
      select: data => {
        const balances = data.tokens;

        if (!Array.isArray(balances) || balances.length === 0) {
          console.warn("Empty balances");
          return [] as SupWeb3Balance[];
        }

        return balances.map(singleBalance => {
          // FixNumber doesn't support more than 18 decimals ?
          const decimals = Math.min(singleBalance.decimals, 18);
          // checking if it's unsupported
          const isUnsupported =
            unsupportedTokensList?.some(token =>
              singleBalance.id === singleBalance.chain ? false : getWeb3Address(singleBalance.id, singleBalance.chain_id) === getWeb3Address(token.address, token.chain_id)
            ) || false;
          const balanceAmount = FixedNumber.from(roundBigNumberValueString(singleBalance.amount_corrected, decimals));

          return {
            chainId: singleBalance.chain_id,
            address: singleBalance.id,
            name: singleBalance.name,
            symbol: singleBalance.symbol,
            amount: balanceAmount.toString(),
            decimals,
            price: singleBalance.price,
            chainName: singleBalance.chain,
            icon: singleBalance.logo_url,
            slug: `${singleBalance.symbol}-${singleBalance.chain_id}`,
            searchIndex: [singleBalance.name, singleBalance.symbol, singleBalance.id, singleBalance.chain_id, singleBalance.chain].join(" ").toLowerCase().replace(/\s+/g, ""),
            amountFixedNumber: balanceAmount,
            amountInFiat: getFormattedFiatAmount(balanceAmount, singleBalance.price),
            isUnsupported,
          } as SupWeb3Balance;
        });
      },
    }
  );

  const filteredData = useMemo(() => {
    if (!hideDust) {
      return data;
    }

    return data?.filter(balance => balance.amountInFiat && parseFloat(balance.amountInFiat) >= 0.01);
  }, [data, hideDust]);

  return {
    data: filteredData,
    isLoading,
    isError,
    mutate,
    error,
  };
}
